import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Eye, EyeOff, X, BookOpen, ArrowRight } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { usePost } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { loginSchema } from "../../utils/schemas/login-schema"
import { toastUtils, showSuccessToast } from "../../utils/toast"
import { tokenManager } from "../../utils/apiComponent/apiClient"
import { getEnrollmentIntent, clearEnrollmentIntent } from "../../utils/enrollmentIntent"
import { Badge } from "../../components/ui/badge"
import { formatPrice } from "../../utils/formatters"
import { useTranslation } from "../../i18n"

export default function LoginPage() {
  const { tObj, t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState("")
  const [enrollmentIntent, setEnrollmentIntent] = useState(null)
  
  // Vérifier si on a un contexte d'enrollment
  useEffect(() => {
    const enrollParam = searchParams.get('enroll')
    if (enrollParam) {
      // Récupérer l'intention d'enrollment depuis sessionStorage
      const intent = getEnrollmentIntent()
      if (intent) {
        setEnrollmentIntent(intent)
      }
    }
  }, [searchParams])


  // Configuration du formulaire avec React Hook Form et validation Zod
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  })

  const { execute: loginExecute, loading: loginLoading } = usePost(endpoints.login, { isPublicRoute: true })
  const { execute: forgotExecute, loading: forgotLoading } = usePost(endpoints.forgotPassword, { isPublicRoute: true })

  const onSubmit = async (data) => {
    try {
      console.log("Form data:", data)
      

      // Tentative de connexion réelle - forcer l'exécution à chaque fois
      const response = await loginExecute(data, { force: true })
      console.log("Login response:", response)
      console.log("User data from response:", response?.user)
      
      if (response?.accessToken) {
        // Stocker les données utilisateur
        if (response.user) {
          console.log("🔍 LOGIN DEBUG - Storing user data:", response.user)
          console.log("🔍 LOGIN DEBUG - User role:", response.user.role)
          console.log("🔍 LOGIN DEBUG - User isAdmin:", response.user.isAdmin)
          localStorage.setItem('userData', JSON.stringify(response.user))
        }
        
        // Utiliser la nouvelle méthode qui gère les deux tokens
        tokenManager.setTokens(response.accessToken, response.refreshToken)
        console.log("Tokens stored successfully", {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken ? "***présent***" : "absent"
        })
        console.log("Token from manager:", tokenManager.getToken())
        
        // Déclencher la mise à jour de l'authentification
        window.dispatchEvent(new CustomEvent('authChange', { 
          detail: { isAuthenticated: true, user: response.user } 
        }))
        
        // Afficher le message de succès uniquement si la connexion réussit
        toastUtils.loginSuccess()
        
        // Vérifier si on a une intention d'enrollment
        const intent = getEnrollmentIntent()
        if (intent) {
          // Rediriger vers la page du cours pour compléter l'enrollment
          setTimeout(() => {
            navigate(`/courses/${intent.courseId}`, { replace: true })
          }, 500)
        } else {
          // Redirection vers la page d'accueil ou returnUrl
          const returnUrl = searchParams.get('returnUrl')
          setTimeout(() => {
            window.location.href = returnUrl || "/"
          }, 1000)
        }
      } else {
        console.warn("No accessToken found in response:", response)
        // Si pas de token, traiter comme une erreur
        toastUtils.loginError(tObj.login.loginError())
      }

    } catch (error) {
      console.error("Login failed:", error)
      toastUtils.loginError(error?.message || tObj.login.loginError())
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    if (!resetEmail) {
      toastUtils.showWarningToast({ message: tObj.login.enterEmailAddress() })
      return
    }
    try {
      await forgotExecute({ email: resetEmail })
      showSuccessToast({ message: t('login.resetEmailSent', { email: resetEmail }) })
      setShowForgotPassword(false)
      setResetEmail("")
    } catch (error) {
      toastUtils.validationError(error?.message || tObj.login.emailSendFailed())
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center p-4 pt-24">
      <div className="flex w-full max-w-6xl min-h-[600px] items-center">
        {/* Section Image - Gauche */}
        <div className="hidden lg:flex lg:w-1/2 p-8">
          <div className="w-full h-full border border-[#D5DAE3] overflow-hidden flex items-center justify-center">
            <img src="/images/students.png" alt={tObj.login.studentsImageAlt()} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#203a8a]/10"></div>
          </div>
        </div>

        {/* Section Formulaire - Droite */}
        <div className="w-full lg:w-1/2 flex items-start justify-center p-4 sm:p-6 lg:p-8 pt-16">
          <div className="w-full max-w-md bg-white border border-[#D5DAE3] p-6 sm:p-8">
            {/* Logo et Titre */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-4">
                <img
                  src="/images/logo.png"
                  alt="AY Solutions Academy"
                  className="h-28 sm:h-32 w-auto object-contain"
                />
              </div>

              {enrollmentIntent ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div className="p-2 bg-[#203a8a]">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="font-display text-2xl lg:text-3xl text-[#203a8a]">
                      {tObj.login.connectToAccessCourse()}
                    </h2>
                  </div>
                  <p className="font-sans text-[#6B7280] text-sm lg:text-base">
                    {tObj.login.createAccountOrLogin()}
                  </p>
                   
                  {/* Course Preview Card */}
                  <div className="mt-4 p-4 bg-[#FAFBFD] border border-[#D5DAE3]">
                    <div className="flex items-center gap-3">
                      {enrollmentIntent.courseThumbnail && (
                        <img 
                          src={enrollmentIntent.courseThumbnail} 
                          alt={enrollmentIntent.courseTitle}
                          className="w-16 h-16 object-cover"
                        />
                      )}
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-[#1B2240] text-sm mb-1">
                          {enrollmentIntent.courseTitle}
                        </h3>
                        <div className="flex items-center gap-2">
                          {enrollmentIntent.isFree ? (
                            <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-[#E8C547] text-[#203a8a] font-semibold">{tObj.login.free()}</span>
                          ) : (
                            <span className="text-sm font-semibold text-[#1B2240]">
                              {tObj.login.paidCourse()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-display text-2xl lg:text-3xl text-[#203a8a] mb-2">
                    {tObj.login.welcomeBack()}
                  </h2>
                  <p className="font-sans text-[#6B7280] text-sm lg:text-base">{tObj.login.pleaseLogin()}</p>
                </>
              )}
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Email */}
              <div className="relative z-10">
                <input
                  type="email"
                  placeholder={tObj.login.emailPlaceholder()}
                  {...register("email")}
                  className="w-full h-12 lg:h-14 bg-white border px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] cursor-pointer"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.email.message}</p>
                )}
              </div>

              {/* Mot de passe */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={tObj.login.passwordPlaceholder()}
                  {...register("password")}
                  className={`w-full h-12 lg:h-14 bg-white border px-4 pr-12 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.password ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-[#203a8a] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.password.message}</p>
                )}
              </div>

              {/* Espacement supplémentaire pour compenser les champs manquants */}
              <div className="py-2">
                {/* Mot de passe oublié */}
                <div className="text-right mb-6">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="relative z-10 text-[10px] uppercase tracking-[0.22em] text-[#203a8a] hover:text-[#E8C547] transition-colors cursor-pointer"
                  >
                    {tObj.login.forgotPassword()}
                  </button>
                </div>

                {/* Bouton Se connecter */}
                <button
                  type="submit"
                  disabled={isSubmitting || loginLoading}
                  className="relative z-10 w-full h-12 lg:h-14 bg-[#203a8a] hover:bg-[#1a2f6e] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.22em] font-semibold transition-colors cursor-pointer"
                >
                  {isSubmitting || loginLoading ? tObj.login.connecting() : tObj.login.login()}
                </button>
              </div>
            </form>

            {/* Lien vers inscription */}
            <div className="text-center mt-6">
              <p className="font-sans text-[#6B7280] text-sm lg:text-base">
                {tObj.login.noAccountYet()}{" "}
                <a 
                  href={enrollmentIntent ? `/signup?enroll=${enrollmentIntent.courseId}` : "/signup"} 
                  className="text-[10px] uppercase tracking-[0.22em] text-[#203a8a] hover:text-[#E8C547] font-semibold inline-flex items-center gap-1 transition-colors"
                >
                  {tObj.login.signup()}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Mot de passe oublié */}
      {showForgotPassword && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowForgotPassword(false)}
          ></div>

          <div className="bg-white border border-[#D5DAE3] p-6 lg:p-8 w-full max-w-md relative z-10">
            {/* Close button */}
            <button
              onClick={() => setShowForgotPassword(false)}
              className="absolute right-4 top-4 text-[#6B7280] hover:text-[#1B2240] transition-colors"
            >
              <X size={24} />
            </button>

            {/* Modal content */}
            <div className="text-center mb-6">
              <h3 className="font-display text-xl lg:text-2xl text-[#203a8a] mb-2">
                {tObj.login.forgotPasswordTitle()}
              </h3>
              <p className="font-sans text-[#6B7280] text-sm lg:text-base">
                {tObj.login.enterEmailForReset()}
              </p>
            </div>

            <div className="space-y-4">
              <input
                type="email"
                placeholder={tObj.login.yourEmail()}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full h-12 lg:h-14 bg-white border border-[#D5DAE3] px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] cursor-pointer"
                required
              />

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="relative z-10 flex-1 h-12 lg:h-14 bg-[#FAFBFD] border border-[#D5DAE3] text-[#1B2240] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E8C547] hover:border-[#E8C547] transition-colors cursor-pointer"
                >
                  {tObj.login.cancel()}
                </button>
                <button
                  type="submit"
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="relative z-10 flex-1 h-12 lg:h-14 bg-[#203a8a] hover:bg-[#1a2f6e] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.22em] font-semibold transition-colors cursor-pointer"
                >
                  {forgotLoading ? tObj.login.sending() : tObj.login.send()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}